import * as i0 from '@angular/core';
import { NgModule, Injectable, Inject } from '@angular/core';
import * as i2 from '@upr-web-primer/digital-data';
import * as i1 from '@upr-web-primer/gds-button-model-service';
import { WINDOW } from '@upr-web-primer/window';
import { get, set, isEqual, cloneDeep } from 'lodash-es';
import { Subscription, fromEvent } from 'rxjs';
class GdsComparisonTableModule {
  static {
    this.ɵfac = function GdsComparisonTableModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GdsComparisonTableModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: GdsComparisonTableModule
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GdsComparisonTableModule, [{
    type: NgModule,
    args: [{}]
  }], null, null);
})();
class GdsComparisonTableService {
  constructor(buttonDataMapper, digitalDataService, window) {
    this.buttonDataMapper = buttonDataMapper;
    this.digitalDataService = digitalDataService;
    this.window = window;
    this.gdsComparisonTableSubscription = new Subscription();
  }
  /**
   * Method to parse Tridion comparison table data,
   * converting it into GDS comparison table data structure.
   *
   * @param gdsComponentContent
   * @param gdsComponent
   * @returns GdsComparisonTableData
   */
  parse(gdsComponentContent, gdsComponent) {
    if (!gdsComponentContent) return;
    const rawResources = get(gdsComponentContent, 'resources', {});
    const resources = Array.isArray(rawResources) ? rawResources[0] : rawResources;
    const formattedResources = get(resources, 'Resource', []).reduce((data, resource) => {
      data[resource.Key] = resource.Value;
      return data;
    }, {});
    this.updateDDLProductsInfo(gdsComponent);
    return {
      accordion: get(gdsComponentContent, 'accordion', false),
      footnote: get(gdsComponentContent, 'footnote'),
      items: this.parseItems(gdsComponentContent.items),
      resources: formattedResources,
      sections: this.parseSections(gdsComponentContent.sections, formattedResources),
      title: get(gdsComponentContent, 'title', ''),
      footnoteAriaLabel: get(gdsComponentContent, 'footnoteAriaLabel', '')
    };
  }
  /**
   * Method to parse Tridion comparison table and set data property on gds-comparison-table element.
   *
   * @param gdsComponentContent
   * @param gdsComponent
   * @returns void
   */
  setComparisonTableData(gdsComponentContent, gdsComponent) {
    const parsedData = this.parse(gdsComponentContent, gdsComponent);
    if (!parsedData) {
      return;
    }
    gdsComponent.data = parsedData;
  }
  /**
   * Method to parse items array.
   *
   * @param items
   * @returns CTItem[]
   */
  parseItems(items) {
    if (!items) {
      return [];
    }
    const itemsArray = new Array().concat(items);
    return itemsArray.map(item => {
      const parsedAttributes = this.parseItemAttribute(item);
      return {
        ...item,
        attributes: parsedAttributes
      };
    });
  }
  /**
   * Method to parse an item's attribute data.
   *
   * @param attribute
   * @returns CTItemAttribute
   */
  parseItemAttribute(item) {
    const rawAttributes = get(item, 'attributes');
    if (!rawAttributes) {
      return [];
    }
    const attributes = new Array().concat(rawAttributes);
    return attributes.map(attribute => {
      const parsedAttribute = {
        icon: get(attribute, 'icon', ''),
        value: get(attribute, 'value', '')
      };
      const innerAttribute = get(attribute, 'attribute');
      const flexContainerMeta = get(innerAttribute, 'Meta');
      const cpMetadata = get(innerAttribute, 'MetadataFields');
      if (flexContainerMeta) {
        set(parsedAttribute, 'key', get(innerAttribute, 'key'));
        set(parsedAttribute, 'title', get(flexContainerMeta, 'title', ''));
        set(parsedAttribute, 'description', get(flexContainerMeta, 'description', ''));
        set(parsedAttribute, 'section', get(flexContainerMeta, 'parent', ''));
        const button = get(flexContainerMeta, 'button');
        if (button) {
          set(parsedAttribute, 'button', this.buttonDataMapper.mapButtonData(button));
        }
      } else if (cpMetadata) {
        set(parsedAttribute, 'key', get(innerAttribute, 'Key'));
        set(parsedAttribute, 'title', get(cpMetadata, 'title.Values[0]', ''));
        set(parsedAttribute, 'description', get(cpMetadata, 'description.Values[0]', ''));
        set(parsedAttribute, 'section', get(cpMetadata, 'parent.KeywordValues[0].Key', ''));
        const button = get(cpMetadata, 'button');
        if (button) {
          set(parsedAttribute, 'button', this.buttonDataMapper.init(button));
        }
      }
      return parsedAttribute;
    });
  }
  /**
   * Method to parse sections array.
   *
   * @param sections
   * @param resources
   * @returns
   */
  parseSections(sections, resources) {
    if (!sections) {
      return;
    }
    let parsedSections = [];
    if (Array.isArray(sections)) {
      parsedSections = sections.map(section => {
        return {
          sectionTitle: get(section, 'Description', ''),
          sectionKey: get(section, 'Key', '')
        };
      });
    } else if (typeof sections === 'object') {
      const keys = get(sections, 'key', '').split(',');
      const flexMeta = get(sections, 'Meta');
      if (flexMeta && typeof flexMeta === 'object') {
        parsedSections = this.parseFlexSection(keys, flexMeta);
      } else {
        parsedSections = [{
          sectionTitle: get(sections, 'MetadataFields.title.Values[0]', ''),
          sectionKey: get(sections, 'Key', '')
        }];
      }
    } else if (typeof sections === 'string') {
      try {
        const sectionsArray = JSON.parse(sections);
        parsedSections = sectionsArray.map(key => ({
          sectionTitle: get(resources, [key], key),
          sectionKey: key
        }));
      } catch {
        parsedSections = [{
          sectionTitle: get(resources, [sections], sections),
          sectionKey: sections
        }];
      }
    }
    return parsedSections;
  }
  /**
   * Method used for parsing flexible container sections metadata from Tridion proxy content API.
   *
   * @param flexMeta
   * @returns
   */
  parseFlexSection(keys, flexMeta) {
    return keys.map(key => ({
      sectionTitle: get(flexMeta, [key, 'title'], ''),
      sectionKey: key
    }));
  }
  updateDDLProductsInfo(gdsComponent) {
    if (gdsComponent) {
      this.gdsComparisonTableSubscription.add(fromEvent(gdsComponent, 'columnsLoaded').subscribe(e => {
        if (!isEqual(this.prev?.columns, e.detail?.columns)) {
          this.prev = cloneDeep(e.detail);
          const headings = this.prev?.columns?.map(column => column.heading);
          const eventInfo = this.digitalDataService.getEventInfoInstance('Compare Annual And Season Passes', e.detail.type, 'Products List', '', new Date().toISOString(), headings.join());
          const eventCategory = this.digitalDataService.getEventCategoryInstance(get(this.window, 'digitalData.page.category.primaryCategory', ''), get(this.window, 'digitalData.page.category.subCategory1', ''));
          const eventData = this.digitalDataService.getEventInstance(eventInfo, eventCategory);
          this.digitalDataService.fireEvent(eventData);
        }
      }));
    }
  }
  static {
    this.ɵfac = function GdsComparisonTableService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || GdsComparisonTableService)(i0.ɵɵinject(i1.GdsButtonModelService), i0.ɵɵinject(i2.DigitalDataService), i0.ɵɵinject(WINDOW));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: GdsComparisonTableService,
      factory: GdsComparisonTableService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(GdsComparisonTableService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1.GdsButtonModelService
  }, {
    type: i2.DigitalDataService
  }, {
    type: Window,
    decorators: [{
      type: Inject,
      args: [WINDOW]
    }]
  }], null);
})();

/*
 * Public API Surface of gds-comparison-table
 */

/**
 * Generated bundle index. Do not edit.
 */

export { GdsComparisonTableModule, GdsComparisonTableService };
